<script>
/**
 * 공지사항·FAQ 목록 (meta.boardKind: 'notice' | 'faq', HQ 전용 라우트)
 */
import * as noticeApi from '@/api/notice.js'
import * as faqApi from '@/api/faq.js'
import AppPagination from '@/components/common/AppPagination.vue'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'SiteBoardListPage',
  components: { AppPagination },
  data() {
    return {
      items: [],
      page: 1,
      size: 20,
      total: 0,
      keyword: '',
      loading: false,
      isHq: false,
    }
  },
  computed: {
    kind() {
      return this.$route.meta.boardKind || 'notice'
    },
    isNotice() {
      return this.kind === 'notice'
    },
    listTitle() {
      return this.isNotice ? '공지사항' : '자주묻는 질문'
    },
  },
  mounted() {
    this.isHq = getAdminInfo().adminType === 'HQ'
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const params = { page: this.page, size: this.size, keyword: this.keyword || undefined }
        const res = this.isNotice ? await noticeApi.getNoticeList(params) : await faqApi.getFaqList(params)
        if (res.data?.code === 0) {
          const d = res.data.data
          this.items = d?.items || []
          this.total = d?.total ?? 0
        }
      } catch (e) {
        console.error('[SiteBoardListPage]', e)
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      this.page = 1
      this.fetchList()
    },
    onPageChange(p) {
      this.page = p
      this.fetchList()
    },
    onSizeChange(s) {
      this.size = s
      this.page = 1
      this.fetchList()
    },
    /** 목록: 날짜만 (시간 제외) */
    formatDateOnly(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },
    goNew() {
      this.$router.push(this.isNotice ? '/notices/new' : '/faqs/new')
    },
    goEdit(row) {
      this.$router.push(this.isNotice ? `/notices/${row.boardCode}` : `/faqs/${row.boardCode}`)
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-2 mb-4">
      <div>
        <div class="text-h5 font-weight-bold">{{ listTitle }}</div>
        <div class="text-body-2 text-grey">앱에 노출되는 {{ listTitle }}을 등록·수정합니다.</div>
      </div>
      <v-btn v-if="isHq" color="primary" variant="flat" @click="goNew">등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mb-4">
      <v-card-text class="d-flex flex-wrap gap-2 align-center">
        <v-text-field
          v-model="keyword"
          label="제목 검색"
          density="compact"
          variant="outlined"
          hide-details
          class="mr-3"
          clearable
          style="max-width: 280px"
          @keyup.enter="onSearch"
        />
        <v-btn color="primary" variant="tonal" @click="onSearch">검색</v-btn>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-progress-linear v-if="loading" indeterminate color="primary" height="3" />
      <v-table density="compact">
        <thead>
          <tr>
            <th v-if="isNotice" style="width: 88px">상단고정</th>
            <th>제목</th>
            <th style="width: 160px">등록일</th>
            <th style="width: 160px">수정일</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in items"
            :key="row.boardCode"
            style="cursor: pointer"
            @click="goEdit(row)"
          >
            <td v-if="isNotice">
              <v-chip v-if="row.noticeYn === 'Y'" size="small" color="primary" variant="tonal">Y</v-chip>
              <span v-else class="text-grey">-</span>
            </td>
            <td>{{ row.boardTitle || '-' }}</td>
            <td class="text-caption text-grey">{{ formatDateOnly(row.insertDt) }}</td>
            <td class="text-caption text-grey">{{ formatDateOnly(row.updateDt) }}</td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td :colspan="isNotice ? 4 : 3" class="text-center text-grey py-8">데이터가 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <AppPagination
        :page="page"
        :size="size"
        :total="total"
        @update:page="onPageChange"
        @update:size="onSizeChange"
      />
    </v-card>
  </div>
</template>
